import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/apiService';
import { Source } from '../../types';

const AGENTS = [
  { num: '01', name: 'News Discovery', desc: 'Finds aluminium industry news from global sources', status: 'Active', statusColor: 'text-primary' },
  { num: '02', name: 'Source Extraction', desc: 'Extracts facts, quotes, statistics and entities', status: 'Active', statusColor: 'text-primary' },
  { num: '03', name: 'Source Comparison', desc: 'Cross-references facts across multiple sources', status: 'Active', statusColor: 'text-primary' },
  { num: '04', name: 'Fact Verification', desc: 'Validates numbers, dates, claims and logic', status: 'Active', statusColor: 'text-primary' },
  { num: '05', name: 'Deep Research', desc: 'Searches for more sources when facts conflict', status: 'On demand', statusColor: 'text-on-surface-variant' },
  { num: '06', name: 'Article Generation', desc: 'Writes articles from verified facts', status: 'Ready', statusColor: 'text-on-surface-variant' },
  { num: '07', name: 'Style Validation', desc: 'Checks editorial tone matches AlCircle style', status: 'Ready', statusColor: 'text-on-surface-variant' },
  { num: '08', name: 'Plagiarism Check', desc: 'Ensures content is original', status: 'Ready', statusColor: 'text-primary' },
  { num: '09', name: 'Rewrite Check', desc: 'Decides if article needs revision', status: 'Standby', statusColor: 'text-on-surface-variant' },
  { num: '10', name: 'Rewrite Agent', desc: 'Revises articles that failed quality checks', status: 'On demand', statusColor: 'text-on-surface-variant' },
  { num: '11', name: 'Image Generation', desc: 'Creates article hero images with AI', status: 'Ready', statusColor: 'text-secondary' },
  { num: '12', name: 'Final QA', desc: 'Last quality review before publishing', status: 'Ready', statusColor: 'text-primary' },
];

const SystemConfig: React.FC = () => {
  const [selectedAgent, setSelectedAgent] = useState('01');
  const [sources, setSources] = useState<Source[]>([]);
  const [newSourceName, setNewSourceName] = useState('');
  const [newSourceUrl, setNewSourceUrl] = useState('');
  const [newSourceCategory, setNewSourceCategory] = useState('RSS Feeds');
  const [showAddSource, setShowAddSource] = useState(false);
  const [sourceLoading, setSourceLoading] = useState<string | null>(null);
  const [sourceError, setSourceError] = useState<string | null>(null);
  const [sourceSuccess, setSourceSuccess] = useState<string | null>(null);

  const flashMsg = (kind: 'error' | 'success', msg: string) => {
    if (kind === 'error') { setSourceError(msg); setTimeout(() => setSourceError(null), 4000); }
    else { setSourceSuccess(msg); setTimeout(() => setSourceSuccess(null), 3000); }
  };

  useEffect(() => {
    apiService.getSources().then(setSources).catch(() => {});
  }, []);

  const refreshSources = async () => {
    const s = await apiService.getSources();
    setSources(s);
  };

  const handleAddSource = async () => {
    if (!newSourceName.trim() || !newSourceUrl.trim()) return;
    setSourceLoading('add');
    try {
      await apiService.addSource(newSourceName.trim(), newSourceUrl.trim(), newSourceCategory);
      setNewSourceName('');
      setNewSourceUrl('');
      setShowAddSource(false);
      await refreshSources();
      flashMsg('success', 'Source added');
    } catch (err) {
      flashMsg('error', 'Failed to add source — ' + (err as Error).message);
    } finally { setSourceLoading(null); }
  };

  const handleToggleSource = async (url: string, is_custom: boolean, enabled: boolean) => {
    setSourceLoading(url);
    try {
      await apiService.toggleSource(url, is_custom, enabled);
      await refreshSources();
    } catch (err) {
      flashMsg('error', 'Failed to update source — ' + (err as Error).message);
    } finally { setSourceLoading(null); }
  };

  const handleRemoveSource = async (url: string) => {
    setSourceLoading(url);
    try {
      await apiService.removeSource(url, true);
      await refreshSources();
      flashMsg('success', 'Source removed');
    } catch (err) {
      flashMsg('error', 'Failed to remove source — ' + (err as Error).message);
    } finally { setSourceLoading(null); }
  };

  const agent = AGENTS.find(a => a.num === selectedAgent) || AGENTS[0];

  return (
    <div className="flex flex-col w-full">

      {/* Toast */}
      {(sourceError || sourceSuccess) && (
        <div
          className="fixed bottom-6 right-6 z-50 flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg font-body text-body-sm"
          style={{
            background: sourceError ? '#fef2f2' : 'rgba(52,211,153,0.12)',
            border: `1px solid ${sourceError ? '#fecaca' : 'rgba(52,211,153,0.4)'}`,
            color: sourceError ? '#dc2626' : '#065f46',
          }}
        >
          <span className="material-symbols-outlined text-[18px]">
            {sourceError ? 'error' : 'check_circle'}
          </span>
          {sourceError || sourceSuccess}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between pb-space-lg">
        <div>
          <h1 className="font-display text-headline-lg text-on-surface tracking-tight">Settings</h1>
          <p className="font-body text-body-md text-on-surface-variant">Manage agents, sources and pipeline configuration</p>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-space-lg items-start pb-space-2xl">
        {/* Left: Agents + Sources */}
        <div className="xl:col-span-7 flex flex-col gap-space-lg">
          {/* Agent List */}
          <div>
            <h2 className="font-display text-headline-sm text-on-surface mb-space-md flex items-center gap-space-xs">
              <span className="material-symbols-outlined text-primary text-[20px]">account_tree</span>
              Pipeline Agents ({AGENTS.length})
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-space-md">
              {AGENTS.map(a => (
                <div
                  key={a.num}
                  onClick={() => setSelectedAgent(a.num)}
                  className={`bg-surface-container p-space-md rounded flex flex-col gap-space-xs cursor-pointer transition-colors ${
                    selectedAgent === a.num ? 'bg-surface-container-high ring-1 ring-primary/40' : 'hover:bg-surface-container-high'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-space-sm">
                      <span className={`w-7 h-7 rounded flex items-center justify-center font-mono text-label-sm ${
                        selectedAgent === a.num ? 'bg-primary text-on-primary font-bold' : 'bg-surface-container-highest text-primary'
                      }`}>{a.num}</span>
                      <span className="font-display text-headline-sm text-on-surface">{a.name}</span>
                    </div>
                    <span className={`font-mono text-label-xs ${a.statusColor}`}>{a.status}</span>
                  </div>
                  <p className="font-body text-body-sm text-on-surface-variant pl-10">{a.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Source Management */}
          <div>
            <div className="flex items-center justify-between mb-space-md">
              <h2 className="font-display text-headline-sm text-on-surface flex items-center gap-space-xs">
                <span className="material-symbols-outlined text-primary text-[20px]">rss_feed</span>
                News Sources ({sources.filter(s => s.enabled !== false).length} active)
              </h2>
              <button
                onClick={() => setShowAddSource(!showAddSource)}
                className="px-space-md py-space-xs bg-primary-container hover:bg-primary text-on-primary font-display text-headline-sm rounded transition-all flex items-center gap-space-xs"
              >
                <span className="material-symbols-outlined text-[16px]">{showAddSource ? 'close' : 'add'}</span>
                {showAddSource ? 'Cancel' : 'Add Source'}
              </button>
            </div>

            {showAddSource && (
              <div className="glass-card p-space-md mb-space-md flex flex-col gap-space-sm">
                <input
                  type="text" placeholder="Source name" value={newSourceName}
                  onChange={e => setNewSourceName(e.target.value)}
                  className="w-full bg-surface-container-lowest text-on-surface p-space-sm rounded font-body text-body-sm focus:outline-none focus:ring-1 focus:ring-primary"
                />
                <input
                  type="url" placeholder="Feed URL (https://...)" value={newSourceUrl}
                  onChange={e => setNewSourceUrl(e.target.value)}
                  className="w-full bg-surface-container-lowest text-on-surface p-space-sm rounded font-body text-body-sm focus:outline-none focus:ring-1 focus:ring-primary"
                />
                <div className="flex gap-space-sm">
                  <select value={newSourceCategory} onChange={e => setNewSourceCategory(e.target.value)}
                    className="flex-1 bg-surface-container-lowest text-on-surface p-space-sm rounded font-body text-body-sm focus:outline-none">
                    <option>RSS Feeds</option>
                    <option>Market Data</option>
                    <option>Company Reports</option>
                    <option>Government & Regulatory</option>
                  </select>
                  <button onClick={handleAddSource} disabled={!newSourceName.trim() || !newSourceUrl.trim() || sourceLoading === 'add'}
                    className="px-space-lg py-space-sm bg-primary text-on-primary font-display text-headline-sm rounded transition-all disabled:opacity-50">
                    {sourceLoading === 'add' ? 'Adding...' : 'Add'}
                  </button>
                </div>
              </div>
            )}

            <div className="glass-card overflow-hidden">
              <div className="divide-y divide-surface-container-high/40">
                {sources.map((source, i) => {
                  const enabled = source.enabled !== false;
                  const loading = sourceLoading === source.url;
                  return (
                    <div key={i} className={`flex items-center gap-space-md px-space-md py-space-sm transition-all ${loading ? 'opacity-50' : ''}`}>
                      <button
                        onClick={() => handleToggleSource(source.url, source.is_custom, !enabled)}
                        disabled={loading}
                        className={`w-9 h-5 rounded-full relative transition-colors flex-shrink-0 ${enabled ? 'bg-primary' : 'bg-surface-container-highest'}`}
                      >
                        <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${enabled ? 'left-[18px]' : 'left-0.5'}`} />
                      </button>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-space-xs">
                          <span className={`font-body text-body-sm ${enabled ? 'text-on-surface' : 'text-outline'}`}>{source.name}</span>
                          {source.is_custom && (
                            <span className="font-mono text-[9px] bg-primary/10 text-primary px-1.5 py-0.5 rounded">CUSTOM</span>
                          )}
                        </div>
                        <span className="font-mono text-label-xs text-outline truncate block">{source.category}</span>
                      </div>
                      {source.is_custom && (
                        <button onClick={() => handleRemoveSource(source.url)} disabled={loading}
                          className="text-error font-mono text-label-xs hover:underline flex-shrink-0">
                          Remove
                        </button>
                      )}
                    </div>
                  );
                })}
                {sources.length === 0 && (
                  <div className="p-space-lg text-center text-outline font-mono text-label-sm">Loading sources...</div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Right: Agent Detail */}
        <div className="xl:col-span-5 flex flex-col gap-space-md">
          <div className="bg-surface-container p-space-lg rounded shadow-xl flex flex-col gap-space-md">
            <div className="pb-space-sm border-b border-surface-container-high">
              <span className="font-mono text-label-xs text-primary tracking-wider uppercase font-bold">Agent #{selectedAgent}</span>
              <h2 className="font-display text-headline-md text-on-surface mt-1">{agent.name}</h2>
              <p className="font-body text-body-sm text-on-surface-variant mt-1">{agent.desc}</p>
            </div>

            <div className="flex items-center justify-between py-space-sm">
              <span className="font-body text-body-sm text-on-surface">Status</span>
              <span className={`font-mono text-label-sm font-semibold ${agent.statusColor}`}>{agent.status}</span>
            </div>

            <div className="flex items-center justify-between py-space-sm border-t border-surface-container-high">
              <span className="font-body text-body-sm text-on-surface">Position in pipeline</span>
              <span className="font-mono text-label-sm text-on-surface">{agent.num} of 12</span>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemConfig;
